// Code Injection Creator Types
// Defines the structure for component templates and user selections

export type ComponentCategory = 
  // Base category (always included)
  | 'base'
  // Single selection categories (mutually exclusive)
  | 'menuItemMovement'
  | 'menuItemGlow'
  | 'categoryHeaderStyle'
  | 'serviceChoiceStyles'
  | 'navigationStyles'
  // Multi-selection categories (combinable)
  | 'menuItemImageEffects'
  | 'menuItemTextEffects'
  | 'categoryHeaderAnimation'
  | 'categoryHeaderTextEffects'
  | 'buttonHoverEffect'
  | 'buttonClickEffect'
  | 'buttonPulseEffect'
  | 'footerEnhancements'
  | 'initialFadeStyle'
  | 'staggerTiming'
  // New Phase 2 categories (multi-selection)
  | 'priceTagEnhancements'
  | 'navBarTextEffects'
  | 'welcomeMessages'
  | 'tagBadgeStyles'
  | 'errorMessageStyles'
  | 'loadingSpinnerStyles'
  | 'searchButtonEffects'
  | 'socialIconEffects'
  | 'scrollBasedEffects'
  // Legacy categories (keep for backward compatibility)
  | 'menuItemAnimations'
  | 'categoryHeaders'
  | 'buttonEffects'
  | 'loadingAnimations'
  | 'effectsEnhancements'
  | 'animationsCombination';

export type AnimationSpeed = 'slow' | 'normal' | 'fast';

export interface SubOption {
  id: string;
  name: string;
  description: string;
}

export interface SubTemplate {
  id: string;
  name: string;
  description: string;
  cssTemplate: string;
  jsTemplate?: string;
  variables: Record<string, string | number>;
}

export interface ComponentTemplate {
  id: string;
  category: ComponentCategory;
  name: string;
  description: string;
  cssTemplate: string;
  jsTemplate?: string;
  variables: Record<string, string | number>;
  preview?: string; // Optional preview description
  conflicts?: string[]; // Array of component IDs that conflict with this one
  stackable?: boolean; // Whether this component can be combined with others in the same category
  group?: string; // Optional grouping for components with sub-options
  subOptions?: SubOption[]; // Sub-options for grouped components
  isGrouped?: boolean; // Whether this template has nested sub-templates
  subTemplates?: SubTemplate[]; // Nested sub-templates for grouped components
}

export interface MenuItemTextStyle {
  color: string;
  fontSize: number;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing: number;
  textShadow: string;
}

export interface ButtonStyle {
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  textTransform: 'none' | 'uppercase' | 'lowercase';
}

export interface ButtonShadowSettings {
  type: 'none' | 'shadow' | 'glow';
  color: string;
  strength: number;
}

export interface SearchButtonStyle {
  backgroundColor: string;
  svgColor: string;
}

export interface GlobalSettings {
  primaryColor: string;
  secondaryColor: string;
  animationSpeed: AnimationSpeed;
  colorMode?: 'light' | 'dark';
  overlayStrength?: number;
  movementStrength?: number;
  glowStrength?: number;
  titleColorStrength?: number;
  descriptionFadeStrength?: number;
  titleStyleStrength?: number;
  categoryHeaderTextColor?: string;
  fontWeight?: number;
  fontSize?: number;
  fontStyle?: 'normal' | 'italic';
  categoryHeaderGradientPrimary?: string;
  categoryHeaderGradientSecondary?: string;
  // New comprehensive text styling
  menuItemTextRest?: MenuItemTextStyle;
  menuItemTextHover?: MenuItemTextStyle;
  // Category underline styling
  categoryUnderlineColor?: string;
  categoryUnderlinePrimaryColor?: string;
  categoryUnderlineSecondaryColor?: string;
  // Nav logo gradient styling
  navLogoGradientPrimary?: string;
  navLogoGradientSecondary?: string;
  // Nav logo glow styling
  navLogoGlowColor?: string;
  // Button styling
  buttonRestState?: ButtonStyle;
  buttonHoverState?: ButtonStyle;
  buttonShadowRest?: ButtonShadowSettings;
  buttonShadowHover?: ButtonShadowSettings;
  // Navigation styling
  navMainBg?: string;
  navMainBorder?: string;
  navTopBg?: string;
  navTopBorder?: string;
  navTopText?: string;
  navBottomBg?: string;
  navBottomBorder?: string;
  navLogoText?: string;
  navSliderBg?: string;
  navSliderText?: string;
  navSearchButtonBg?: string;
  navSearchButtonText?: string;
  // Price tag styling
  priceTagBackgroundColor?: string;
  priceTagTextColor?: string;
  // Price tag gradient styling
  priceTagGradientDirection?: string;
  priceTagGradientStartColor?: string;
  priceTagGradientEndColor?: string;
  priceTagGradientTextColor?: string;
  // Welcome message styling
  welcomeMessageMorningText?: string;
  welcomeMessageAfternoonText?: string;
  welcomeMessageEveningText?: string;
  welcomeMessageSecondaryText?: string;
  welcomeMessageMorningHour?: number;
  welcomeMessageAfternoonHour?: number;
  welcomeMessageEveningHour?: number;
  welcomeMessageTextColor?: string;
  welcomeMessageGradientStartColor?: string;
  welcomeMessageGradientEndColor?: string;
  welcomeMessageBorderColor?: string;
  // Restaurant context
  restaurantName?: string;
  // Search button styling
  searchButtonRestState?: SearchButtonStyle;
  searchButtonHoverState?: SearchButtonStyle;
  // Enhanced tag badge colors (for all tags)
  tagBadgeEnhancedPrimaryColor?: string;
  tagBadgeEnhancedSecondaryColor?: string;
  // Tag badge colors for dietary tags (special styling)
  tagBadgeVeganPrimaryColor?: string;
  tagBadgeVeganSecondaryColor?: string;
  tagBadgeVegetarianPrimaryColor?: string;
  tagBadgeVegetarianSecondaryColor?: string;
  tagBadgeGlutenFreePrimaryColor?: string;
  tagBadgeGlutenFreeSecondaryColor?: string;
  tagBadgeSpicyPrimaryColor?: string;
  tagBadgeSpicySecondaryColor?: string;
  tagBadgeDairyFreePrimaryColor?: string;
  tagBadgeDairyFreeSecondaryColor?: string;
  tagBadgeNutFreePrimaryColor?: string;
  tagBadgeNutFreeSecondaryColor?: string;
  tagBadgeHalalPrimaryColor?: string;
  tagBadgeHalalSecondaryColor?: string;
  // Popular tag colors
  tagBadgePopularPrimaryColor?: string;
  tagBadgePopularSecondaryColor?: string;
  // New tag colors
  tagBadgeNewPrimaryColor?: string;
  tagBadgeNewSecondaryColor?: string;
  // Deal tag colors
  tagBadgeDealPrimaryColor?: string;
  tagBadgeDealSecondaryColor?: string;
  // Theme mode
  themeMode?: 'light' | 'dark'; // Default to 'dark' for backwards compatibility
}

export interface ComponentSelection {
  id: string;
  subOption?: string; // For grouped components with sub-selections
  subTemplate?: string; // For nested sub-template selections
}

export interface SelectedComponents {
  // Single selection categories (string | null)
  menuItemMovement?: string | null;
  menuItemGlow?: string | null;
  categoryHeaderStyle?: string | null;
  serviceChoiceStyles?: string | null;
  navigationStyles?: string | null;
  
  // Multi-selection categories (ComponentSelection[] for new grouped structure, string[] for backward compatibility)
  menuItemImageEffects?: (string | ComponentSelection)[];
  menuItemTextEffects?: string[];
  categoryHeaderAnimation?: string[];
  categoryHeaderTextEffects?: string[];
  buttonHoverEffect?: string[];
  buttonClickEffect?: string[];
  buttonPulseEffect?: string | null;
  footerEnhancements?: string[];
  initialFadeStyle?: string[];
  staggerTiming?: string[];
  
  // New Phase 2 categories
  priceTagEnhancements?: string | null; // Single selection
  navBarTextEffects?: string[];
  welcomeMessages?: string[];
  tagBadgeStyles?: string[];
  errorMessageStyles?: string[];
  loadingSpinnerStyles?: string[];
  searchButtonEffects?: string[];
  socialIconEffects?: string[];
  scrollBasedEffects?: string[];
  
  // Legacy support (for backward compatibility)
  [category: string]: string | string[] | (string | ComponentSelection)[] | null;
}

export interface GeneratedCode {
  headCode: string;
  bodyCode: string;
  stats: {
    headSize: number;
    bodySize: number;
    totalComponents: number;
  };
}

export interface CodeInjectionState {
  selectedComponents: SelectedComponents;
  globalSettings: GlobalSettings;
  generatedCode: GeneratedCode;
}

// Component category definitions with display information
export interface CategoryInfo {
  id: ComponentCategory;
  name: string;
  description: string;
  icon: string;
  priority: number; // For ordering in UI
  selectionType: 'single' | 'multiple'; // UI selection type
  grouping?: string; // Optional grouping for related categories
  allowMultiple?: boolean; // Legacy support - deprecated, use selectionType
  subcategories?: CategoryInfo[]; // Sub-categories for better organization
}

// Animation speed mappings
export const ANIMATION_SPEEDS: Record<AnimationSpeed, number> = {
  slow: 0.5,
  normal: 0.3,
  fast: 0.15
};

// Default text style for menu items
export const DEFAULT_MENU_ITEM_TEXT_REST: MenuItemTextStyle = {
  color: '#ffffff',
  fontSize: 18,
  fontWeight: 600,
  fontStyle: 'normal',
  textTransform: 'none',
  letterSpacing: 0,
  textShadow: 'none'
};

export const DEFAULT_MENU_ITEM_TEXT_HOVER: MenuItemTextStyle = {
  color: '#c5b068',
  fontSize: 18,
  fontWeight: 600,
  fontStyle: 'normal',
  textTransform: 'none',
  letterSpacing: 0,
  textShadow: 'none'
};

// Default button styles
export const DEFAULT_BUTTON_REST_STATE: ButtonStyle = {
  textColor: '#ffffff',
  backgroundColor: '#c5b068',
  borderColor: 'transparent',
  textTransform: 'none'
};

export const DEFAULT_BUTTON_HOVER_STATE: ButtonStyle = {
  textColor: '#ffffff', 
  backgroundColor: '#a59456',
  borderColor: 'transparent',
  textTransform: 'none'
};

// Default button shadow settings
export const DEFAULT_BUTTON_SHADOW_REST: ButtonShadowSettings = {
  type: 'none',
  color: '#000000',
  strength: 0.3
};

export const DEFAULT_BUTTON_SHADOW_HOVER: ButtonShadowSettings = {
  type: 'shadow',
  color: '#000000',
  strength: 0.4
};

// Default search button styles
export const DEFAULT_SEARCH_BUTTON_REST_STATE: SearchButtonStyle = {
  backgroundColor: '#c5b068', // Global primary color
  svgColor: '#ffffff'
};

export const DEFAULT_SEARCH_BUTTON_HOVER_STATE: SearchButtonStyle = {
  backgroundColor: '#a59456', // Global secondary color
  svgColor: '#ffffff'
};

// Default global settings
export const DEFAULT_GLOBAL_SETTINGS: GlobalSettings = {
  primaryColor: '#c5b068',
  secondaryColor: '#a59456',
  animationSpeed: 'normal',
  colorMode: 'dark',
  overlayStrength: 0.3,
  movementStrength: 1.0,
  glowStrength: 1.0,
  titleColorStrength: 1.0,
  descriptionFadeStrength: 1.0,
  titleStyleStrength: 1.0,
  categoryHeaderTextColor: '#f0f0f0',
  fontWeight: 700,
  fontSize: 28,
  fontStyle: 'normal',
  categoryHeaderGradientPrimary: '#c5b068',
  categoryHeaderGradientSecondary: '#a59456',
  menuItemTextRest: DEFAULT_MENU_ITEM_TEXT_REST,
  menuItemTextHover: DEFAULT_MENU_ITEM_TEXT_HOVER,
  categoryUnderlineColor: '#c5b068',
  categoryUnderlinePrimaryColor: '#c5b068',
  categoryUnderlineSecondaryColor: '#a59456',
  navLogoGradientPrimary: '#c5b068',
  navLogoGradientSecondary: '#a59456',
  navLogoGlowColor: '#c5b068',
  buttonRestState: DEFAULT_BUTTON_REST_STATE,
  buttonHoverState: DEFAULT_BUTTON_HOVER_STATE,
  buttonShadowRest: DEFAULT_BUTTON_SHADOW_REST,
  buttonShadowHover: DEFAULT_BUTTON_SHADOW_HOVER,
  // Price tag styling defaults
  priceTagBackgroundColor: '#c5b068',
  priceTagTextColor: '#ffffff',
  // Price tag gradient defaults
  priceTagGradientDirection: '135deg',
  priceTagGradientStartColor: '#c5b068',
  priceTagGradientEndColor: '#a59456',
  priceTagGradientTextColor: '#ffffff',
  // Welcome message defaults
  welcomeMessageMorningText: 'Good morning!',
  welcomeMessageAfternoonText: 'Good afternoon!',
  welcomeMessageEveningText: 'Good evening!',
  welcomeMessageSecondaryText: 'Welcome to {restaurant name}!',
  welcomeMessageMorningHour: 12,
  welcomeMessageAfternoonHour: 17,
  welcomeMessageEveningHour: 24,
  welcomeMessageTextColor: '#ffffff',
  welcomeMessageGradientStartColor: '#c5b068',
  welcomeMessageGradientEndColor: '#a59456',
  welcomeMessageBorderColor: 'transparent',
  // Restaurant context defaults
  restaurantName: 'our restaurant',
  // Search button defaults
  searchButtonRestState: DEFAULT_SEARCH_BUTTON_REST_STATE,
  searchButtonHoverState: DEFAULT_SEARCH_BUTTON_HOVER_STATE,
  // Enhanced tag badge defaults (uses primary/secondary by default)
  tagBadgeEnhancedPrimaryColor: '#c5b068',
  tagBadgeEnhancedSecondaryColor: '#a59456',
  // Tag badge color defaults (special dietary tags)
  tagBadgeVeganPrimaryColor: '#26c526',
  tagBadgeVeganSecondaryColor: '#166a16',
  tagBadgeVegetarianPrimaryColor: '#32CD32',
  tagBadgeVegetarianSecondaryColor: '#36AB36',
  tagBadgeGlutenFreePrimaryColor: '#FFB347',
  tagBadgeGlutenFreeSecondaryColor: '#FF8C00',
  tagBadgeSpicyPrimaryColor: '#FF6B6B',
  tagBadgeSpicySecondaryColor: '#FF3333',
  tagBadgeDairyFreePrimaryColor: '#87CEEB',
  tagBadgeDairyFreeSecondaryColor: '#4682B4',
  tagBadgeNutFreePrimaryColor: '#DEB887',
  tagBadgeNutFreeSecondaryColor: '#8B7355',
  tagBadgeHalalPrimaryColor: '#019000',
  tagBadgeHalalSecondaryColor: '#B8860B',
  // Popular tag defaults (Purple to Red gradient)
  tagBadgePopularPrimaryColor: '#b400fa',
  tagBadgePopularSecondaryColor: '#ff0000',
  // New tag defaults (Red to Blue gradient)
  tagBadgeNewPrimaryColor: '#ff0000',
  tagBadgeNewSecondaryColor: '#3f92ff',
  // Deal tag defaults (Green to Red gradient)
  tagBadgeDealPrimaryColor: '#4fc060',
  tagBadgeDealSecondaryColor: '#ff0000'
};

// Category information for UI display
export const CATEGORY_INFO: Record<ComponentCategory, CategoryInfo> = {
  // Menu Item Effects (Group 1)
  menuItemMovement: {
    id: 'menuItemMovement',
    name: 'Menu Item Movement',
    description: 'Base hover movement effects',
    icon: '🎯',
    priority: 1,
    selectionType: 'single',
    grouping: 'menuItem',
    allowMultiple: false
  },
  menuItemGlow: {
    id: 'menuItemGlow',
    name: 'Menu Item Glow',
    description: 'Glowing and shadow effects',
    icon: '✨',
    priority: 2,
    selectionType: 'single',
    grouping: 'menuItem',
    allowMultiple: false
  },
  menuItemImageEffects: {
    id: 'menuItemImageEffects',
    name: 'Menu Item Image Effects',
    description: 'Image hover enhancements',
    icon: '🖼️',
    priority: 3,
    selectionType: 'multiple',
    grouping: 'menuItem',
    allowMultiple: true
  },
  menuItemTextEffects: {
    id: 'menuItemTextEffects',
    name: 'Menu Item Text Effects',
    description: 'Text styling and animations',
    icon: '📝',
    priority: 4,
    selectionType: 'multiple',
    grouping: 'menuItem',
    allowMultiple: true
  },
  
  // Category Headers (Group 2)
  categoryHeaderStyle: {
    id: 'categoryHeaderStyle',
    name: 'Category Underline Style',
    description: 'Base styling for category titles',
    icon: '📋',
    priority: 5,
    selectionType: 'single',
    grouping: 'categoryHeader',
    allowMultiple: false
  },
  categoryHeaderAnimation: {
    id: 'categoryHeaderAnimation',
    name: 'Category Header Animation',
    description: 'Animated header effects',
    icon: '🎭',
    priority: 6,
    selectionType: 'multiple',
    grouping: 'categoryHeader',
    allowMultiple: true
  },
  categoryHeaderTextEffects: {
    id: 'categoryHeaderTextEffects',
    name: 'Category Header Text Effects',
    description: 'Text styling effects for category headers',
    icon: '🌈',
    priority: 7,
    selectionType: 'multiple',
    grouping: 'categoryHeader',
    allowMultiple: true
  },
  
  // Button Effects (Group 3)
  buttonHoverEffect: {
    id: 'buttonHoverEffect',
    name: 'Button Hover Effects',
    description: 'Button hover animations',
    icon: '🔘',
    priority: 8,
    selectionType: 'single',
    grouping: 'button',
    allowMultiple: false
  },
  buttonClickEffect: {
    id: 'buttonClickEffect',
    name: 'Button Click Effects',
    description: 'Button click animations',
    icon: '👆',
    priority: 9,
    selectionType: 'multiple',
    grouping: 'button',
    allowMultiple: true
  },
  buttonPulseEffect: {
    id: 'buttonPulseEffect',
    name: 'Button Pulse Effects',
    description: 'Continuous button animations',
    icon: '💓',
    priority: 10,
    selectionType: 'single',
    grouping: 'button',
    allowMultiple: false
  },
  
  // Page Loading (Group 4)
  initialFadeStyle: {
    id: 'initialFadeStyle',
    name: 'Initial Fade Style',
    description: 'Page load fade animations',
    icon: '🌅',
    priority: 11,
    selectionType: 'multiple',
    grouping: 'loading',
    allowMultiple: true
  },
  staggerTiming: {
    id: 'staggerTiming',
    name: 'Stagger Timing',
    description: 'Staggered animation timing',
    icon: '⏱️',
    priority: 12,
    selectionType: 'multiple',
    grouping: 'loading',
    allowMultiple: true
  },
  
  // Other Elements (Group 5)
  navigationStyles: {
    id: 'navigationStyles',
    name: 'Navigation Styles',
    description: 'Navigation bar enhancements',
    icon: '🧭',
    priority: 13,
    selectionType: 'single',
    grouping: 'other',
    allowMultiple: false
  },
  serviceChoiceStyles: {
    id: 'serviceChoiceStyles',
    name: 'Service Choice Styles',
    description: 'Delivery/pickup selection styling',
    icon: '🚚',
    priority: 14,
    selectionType: 'single',
    grouping: 'other',
    allowMultiple: false
  },
  footerEnhancements: {
    id: 'footerEnhancements',
    name: 'Footer Enhancements',
    description: 'Footer decorative elements',
    icon: '⬇️',
    priority: 15,
    selectionType: 'multiple',
    grouping: 'other',
    allowMultiple: true
  },
  
  // New Phase 2 categories
  priceTagEnhancements: {
    id: 'priceTagEnhancements',
    name: 'Price Tag Enhancements',
    description: 'Enhanced price tag styling with gradients and animations',
    icon: '🏷️',
    priority: 17,
    selectionType: 'single',
    grouping: 'advanced',
    allowMultiple: false
  },
  navBarTextEffects: {
    id: 'navBarTextEffects',
    name: 'Nav Bar Text Effects',
    description: 'Logo text gradients and navigation enhancements',
    icon: '📝',
    priority: 17,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  welcomeMessages: {
    id: 'welcomeMessages',
    name: 'Welcome Messages',
    description: 'Time-based greeting notifications',
    icon: '👋',
    priority: 18,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  tagBadgeStyles: {
    id: 'tagBadgeStyles',
    name: 'Tag Badge Styles',
    description: 'Enhanced styling for food tags and badges',
    icon: '🏷️',
    priority: 19,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  errorMessageStyles: {
    id: 'errorMessageStyles',
    name: 'Error Message Styles',
    description: 'Warning and error message enhancements',
    icon: '⚠️',
    priority: 20,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  loadingSpinnerStyles: {
    id: 'loadingSpinnerStyles',
    name: 'Loading Spinner Styles',
    description: 'Custom loading spinner colors and animations',
    icon: '⏳',
    priority: 21,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  searchButtonEffects: {
    id: 'searchButtonEffects',
    name: 'Search Button Effects',
    description: 'Search button hover animations and styling',
    icon: '🔍',
    priority: 22,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  socialIconEffects: {
    id: 'socialIconEffects',
    name: 'Social Icon Effects',
    description: 'Footer social icon animations and hover effects',
    icon: '📱',
    priority: 23,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  scrollBasedEffects: {
    id: 'scrollBasedEffects',
    name: 'Scroll Based Effects',
    description: 'Navigation changes and animations on scroll',
    icon: '📜',
    priority: 25,
    selectionType: 'multiple',
    grouping: 'advanced',
    allowMultiple: true
  },
  
  // Legacy categories (for backward compatibility)
  menuItemAnimations: {
    id: 'menuItemAnimations',
    name: 'Menu Item Animations',
    description: 'Hover effects for menu items',
    icon: '🎯',
    priority: 26,
    selectionType: 'single',
    allowMultiple: false
  },
  categoryHeaders: {
    id: 'categoryHeaders',
    name: 'Category Headers',
    description: 'Styling for category titles',
    icon: '📋',
    priority: 27,
    selectionType: 'single',
    allowMultiple: false
  },
  buttonEffects: {
    id: 'buttonEffects',
    name: 'Button Effects',
    description: 'Interactive button animations',
    icon: '🔘',
    priority: 28,
    selectionType: 'single',
    allowMultiple: false
  },
  loadingAnimations: {
    id: 'loadingAnimations',
    name: 'Loading Animations',
    description: 'Page loading and transition effects',
    icon: '⏳',
    priority: 29,
    selectionType: 'single',
    allowMultiple: false
  },
  effectsEnhancements: {
    id: 'effectsEnhancements',
    name: 'Effects Enhancements',
    description: 'Stackable visual effects that can be combined',
    icon: '✨',
    priority: 30,
    selectionType: 'multiple',
    allowMultiple: true
  },
  animationsCombination: {
    id: 'animationsCombination',
    name: 'Animation Combinations',
    description: 'Multiple animations that work together',
    icon: '🎭',
    priority: 31,
    selectionType: 'multiple',
    allowMultiple: true
  }
};

// Preset configuration types
export interface CodeInjectionPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  selectedComponents: SelectedComponents;
  globalSettings: GlobalSettings;
  category: 'minimal' | 'balanced' | 'advanced';
  restaurantTypes?: string[]; // Suggested restaurant types for this preset
}

// Validation result types
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  conflicts?: ConflictInfo[];
}

export interface ConflictInfo {
  componentId: string;
  conflictsWith: string[];
  category: ComponentCategory;
  severity: 'error' | 'warning';
}

// Migration utility types
export interface MigrationResult {
  success: boolean;
  migratedComponents: SelectedComponents;
  skippedComponents: string[];
  errors: string[];
}

// Selection mode types
export type SelectionMode = 'single' | 'multiple';

// Helper type for component selection handling
export type ComponentSelection = string | string[] | null;

// Enhanced global settings with more customisation options
export interface EnhancedGlobalSettings extends GlobalSettings {
  accentColor?: string;
  fontFamily?: string;
  borderRadius?: number;
  spacing?: number;
}